import { useState } from 'react';
import { StepOne } from './CheckoutSteps/StepOne';
import { StepTwo } from './CheckoutSteps/StepTwo';
import { StepThree } from './CheckoutSteps/StepThree';
import { useTheme } from '../hooks/useTheme'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { stepOneContent } from '../data/stepone';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
}

export function CheckoutModal({ isOpen, onClose }: CheckoutModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isStepValid, setIsStepValid] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: ''
  });
  
  const steps = [
    { number: 1, title: 'Formulário' },
    { number: 2, title: 'Envio dos Arquivos' },
    { number: 3, title: 'Finalização' }
  ];

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    setCurrentStep(1);
    onClose();
  };

  // Função para avançar para o próximo step
  const handleStepValidation = (isValid: boolean) => {
    setIsStepValid(isValid);
    
    // Se estiver no Step 2 e for válido, avança automaticamente
    if (currentStep === 2 && isValid) {
      setCurrentStep(3);
    }
  };

  const theme = useTheme(state => state.getThemeStyles())

  if (!isOpen) return null;

  return (
    <div className={theme.components.modal.wrapper}>
      {/* Overlay */}
      <div 
        className={theme.components.modal.overlay}
        onClick={onClose}
      />
      
      <div className={theme.components.modal.container}>
        {/* Botão de fechar */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-400 hover:text-red-400 transition-colors duration-300"
          aria-label="Fechar modal"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>

        <header className={theme.components.modal.header.wrapper}>
          <div className={theme.components.modal.header.steps.wrapper}>
            {steps.map((step) => (
              <div key={step.number} className={`
                ${theme.components.modal.header.steps.item.base}
                ${currentStep === step.number 
                  ? theme.components.modal.header.steps.item.active
                  : currentStep > step.number 
                    ? theme.components.modal.header.steps.item.completed
                    : theme.components.modal.header.steps.item.inactive
                }
              `}>
                <span className={theme.components.modal.header.steps.item.number}>
                  {step.number}
                </span>
                <span className={theme.components.modal.header.steps.item.title}>
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </header>

        <main className={theme.components.modal.content.wrapper}>
          {currentStep === 1 && (
            <div className={theme.components.modal.content.grid}>
              <div className={theme.components.modal.content.left.wrapper}>
                <h3 className={theme.components.modal.content.left.title}>
                  {stepOneContent.title}
                </h3>
                <p className={theme.components.modal.content.left.description}>
                  {stepOneContent.description}
                </p>
              </div>
              <div className={theme.components.modal.content.right.wrapper}>
                <StepOne 
                  formData={formData} 
                  onValidChange={handleStepValidation}
                  onFormChange={setFormData}
                />
              </div>
            </div>
          )}
          
          {currentStep === 2 && (
            <div className={theme.components.modal.content.centered}>
              <StepTwo formData={formData} onValidChange={handleStepValidation} />
            </div>
          )}
          
          {currentStep === 3 && (
            <div className={theme.components.modal.content.centered}>
              <StepThree />
            </div>
          )}
        </main>

        <footer className={theme.components.modal.footer.wrapper}>
          <div className={`
            ${theme.components.modal.footer.content}
            ${currentStep === 3 ? 'justify-center' : 'justify-end'}
          `}>
            {currentStep === 1 && (
              <button
                onClick={handleNext}
                disabled={!isStepValid}
                className={`
                  ${theme.components.modal.footer.button.base}
                  ${isStepValid 
                    ? theme.components.modal.footer.button.enabled 
                    : theme.components.modal.footer.button.disabled}
                  w-[120px]
                `}
              >
                Avançar
              </button>
            )}
            
            {currentStep === 2 && (
              <button
                onClick={handleBack}
                className={`
                  ${theme.components.modal.footer.button.base}
                  ${theme.components.modal.footer.button.enabled}
                  w-[120px]
                `}
              >
                Voltar
              </button>
            )}
            
            {currentStep === 3 && (
              <button
                onClick={handleFinish}
                className={`
                  ${theme.components.modal.footer.button.base}
                  ${theme.components.modal.footer.button.enabled}
                  w-[120px] mx-auto
                `}
              >
                Finalizar
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}